import React, { useState } from 'react'
import { AppShell } from '@/components/shared/AppShell'
import { router, Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  FolderTree,
  BookOpen,
  Layers,
  ChevronRight,
  Plus,
  Pencil,
  Trash2,
  ArrowLeft,
  GraduationCap,
  Sparkles,
  Link2,
  Bookmark,
  HelpCircle,
  FileText,
  Download,
  Upload
} from 'lucide-react'

import { resolveBackNavigation } from '@/lib/utils'
import { ImportSyllabusModal } from '@/components/admin/ImportSyllabusModal'


interface Standard {
  id: number
  name: string
}

interface MappedSection {
  id: number
  name: string
}

interface SyllabusSectionData {
  id: number
  name: string
  section_ids: number[]
  questions_count?: number
  mapped_sections: MappedSection[]
}

interface SyllabusChapterData {
  id: number
  name: string
  chapter_id: number | null
  chapter_name: string | null
  standard_ids: number[]
  standards: Standard[]
  questions_count?: number
  sections: SyllabusSectionData[]
}

interface SyllabusSubjectData {
  id: number
  name: string
  subject_id: number | null
  subject_name: string | null
  questions_count?: number
  master_chapters_count?: number
  master_sections_count?: number
  chapters: SyllabusChapterData[]
}

interface SyllabusDetail {
  id: number
  name: string
  version: number
  description: string | null
  exam_id: number
  exam_name: string | null
  created_at: string
  total_questions_count?: number
  subjects: SyllabusSubjectData[]
}

interface ExamOption { id: number; name: string; code: string }
interface SubjectOption { id: number; name: string; code: string }
interface ChapterOption {
  id: number
  name: string
  subject_id: number | null
  subject_name: string | null
  standard_ids: number[]
  standards: Standard[]
}
interface SectionOption { id: number; name: string; chapter_id: number }

interface Props {
  syllabus: SyllabusDetail
  available_exams: ExamOption[]
  available_standards: Standard[]
  available_subjects: SubjectOption[]
  available_chapters: ChapterOption[]
  available_sections: SectionOption[]
}

export default function SyllabusShow({
  syllabus,
  available_exams,
  available_standards,
  available_subjects,
  available_chapters,
  available_sections,
}: Props) {
  // Modal states
  const [syllabusModalOpen, setSyllabusModalOpen] = useState(false)
  const [editSyllabusName, setEditSyllabusName] = useState(syllabus.name)
  const [editSyllabusVersion, setEditSyllabusVersion] = useState(syllabus.version)
  const [editSyllabusDesc, setEditSyllabusDesc] = useState(syllabus.description || '')
  const [editSyllabusExamId, setEditSyllabusExamId] = useState(syllabus.exam_id)

  // Subject Modal
  const [subjectModalOpen, setSubjectModalOpen] = useState(false)
  const [editingSubjectId, setEditingSubjectId] = useState<number | null>(null)
  const [subjectName, setSubjectName] = useState('')
  const [subjectId, setSubjectId] = useState<number | ''>('')

  // Chapter Modal
  const [chapterModalOpen, setChapterModalOpen] = useState(false)
  const [currentSubjectIdForChapter, setCurrentSubjectIdForChapter] = useState<number | null>(null)
  const [editingChapterId, setEditingChapterId] = useState<number | null>(null)
  const [chapterName, setChapterName] = useState('')
  const [masterChapterId, setMasterChapterId] = useState<number | ''>('')
  const [chapterStandardIds, setChapterStandardIds] = useState<number[]>([])

  // Section Modal
  const [sectionModalOpen, setSectionModalOpen] = useState(false)
  const [currentChapterIdForSection, setCurrentChapterIdForSection] = useState<number | null>(null)
  const [editingSectionId, setEditingSectionId] = useState<number | null>(null)
  const [sectionName, setSectionName] = useState('')
  const [sectionMappedIds, setSectionMappedIds] = useState<number[]>([])

  // Import Modal
  const [importModalOpen, setImportModalOpen] = useState(false)


  // Counts
  const totalChapters = syllabus.subjects.reduce((acc, s) => acc + s.chapters.length, 0)
  const totalSections = syllabus.subjects.reduce(
    (acc, s) => acc + s.chapters.reduce((cAcc, c) => cAcc + c.sections.length, 0),
    0
  )

  // Handlers for Syllabus
  const handleSaveSyllabus = (e: React.FormEvent) => {
    e.preventDefault()
    router.patch(`/admin/syllabi/${syllabus.id}`, {
      syllabus: {
        name: editSyllabusName,
        version: editSyllabusVersion,
        description: editSyllabusDesc || null,
        exam_id: editSyllabusExamId,
      }
    }, {
      onSuccess: () => setSyllabusModalOpen(false)
    })
  }

  // Find active subject for chapter modal
  const currentSubjectForChapter = syllabus.subjects.find((s) => s.id === currentSubjectIdForChapter)
  const filteredChapters = currentSubjectForChapter?.subject_id
    ? available_chapters.filter((c) => c.subject_id === currentSubjectForChapter.subject_id)
    : available_chapters

  // Find active chapter for section modal
  let currentChapterForSection: SyllabusChapterData | undefined
  let currentParentSubjectForSection: SyllabusSubjectData | undefined
  for (const s of syllabus.subjects) {
    const found = s.chapters.find((c) => c.id === currentChapterIdForSection)
    if (found) {
      currentChapterForSection = found
      currentParentSubjectForSection = s
      break
    }
  }

  const filteredSections = currentChapterForSection?.chapter_id
    ? available_sections.filter((sec) => sec.chapter_id === currentChapterForSection.chapter_id)
    : (currentParentSubjectForSection?.subject_id
      ? available_sections.filter((sec) => {
          const ch = available_chapters.find((ac) => ac.id === sec.chapter_id)
          return ch?.subject_id === currentParentSubjectForSection.subject_id
        })
      : available_sections)

  // Handlers for Subject
  const openNewSubject = () => {
    setEditingSubjectId(null)
    setSubjectName('')
    setSubjectId('')
    setSubjectModalOpen(true)
  }

  const openEditSubject = (subj: SyllabusSubjectData) => {
    setEditingSubjectId(subj.id)
    setSubjectName(subj.name)
    setSubjectId(subj.subject_id ?? '')
    setSubjectModalOpen(true)
  }

  const handleSaveSubject = (e: React.FormEvent) => {
    e.preventDefault()
    if (!subjectName.trim()) return

    const payload = {
      syllabus_subject: {
        name: subjectName.trim(),
        subject_id: subjectId === '' ? null : subjectId,
      }
    }

    if (editingSubjectId) {
      router.patch(`/admin/syllabi/${syllabus.id}/syllabus_subjects/${editingSubjectId}`, payload, {
        onSuccess: () => setSubjectModalOpen(false)
      })
    } else {
      router.post(`/admin/syllabi/${syllabus.id}/syllabus_subjects`, payload, {
        onSuccess: () => setSubjectModalOpen(false)
      })
    }
  }

  const handleDeleteSubject = (subjId: number) => {
    if (!confirm('Are you sure you want to remove this syllabus subject and its chapters?')) return
    router.delete(`/admin/syllabi/${syllabus.id}/syllabus_subjects/${subjId}`)
  }

  // Handlers for Chapter
  const openNewChapter = (subjectId: number, defaultSubjectName?: string) => {
    setCurrentSubjectIdForChapter(subjectId)
    setEditingChapterId(null)
    setChapterName('')
    setMasterChapterId('')
    setChapterStandardIds([])
    setChapterModalOpen(true)
  }

  const openEditChapter = (subjectId: number, chap: SyllabusChapterData) => {
    setCurrentSubjectIdForChapter(subjectId)
    setEditingChapterId(chap.id)
    setChapterName(chap.name)
    setMasterChapterId(chap.chapter_id ?? '')
    setChapterStandardIds(chap.standard_ids || [])
    setChapterModalOpen(true)
  }

  const handleSaveChapter = (e: React.FormEvent) => {
    e.preventDefault()
    if (!chapterName.trim() || !currentSubjectIdForChapter) return

    const payload = {
      syllabus_subject_id: currentSubjectIdForChapter,
      syllabus_chapter: {
        name: chapterName.trim(),
        chapter_id: masterChapterId === '' ? null : masterChapterId,
        standard_ids: chapterStandardIds,
      }
    }

    if (editingChapterId) {
      router.patch(`/admin/syllabi/${syllabus.id}/syllabus_chapters/${editingChapterId}`, payload, {
        onSuccess: () => setChapterModalOpen(false)
      })
    } else {
      router.post(`/admin/syllabi/${syllabus.id}/syllabus_chapters`, payload, {
        onSuccess: () => setChapterModalOpen(false)
      })
    }
  }

  const handleDeleteChapter = (chapId: number) => {
    if (!confirm('Are you sure you want to remove this syllabus chapter and its sections?')) return
    router.delete(`/admin/syllabi/${syllabus.id}/syllabus_chapters/${chapId}`)
  }

  // Handlers for Section
  const openNewSection = (chapId: number) => {
    setCurrentChapterIdForSection(chapId)
    setEditingSectionId(null)
    setSectionName('')
    setSectionMappedIds([])
    setSectionModalOpen(true)
  }

  const openEditSection = (chapId: number, sec: SyllabusSectionData) => {
    setCurrentChapterIdForSection(chapId)
    setEditingSectionId(sec.id)
    setSectionName(sec.name)
    setSectionMappedIds(sec.section_ids || [])
    setSectionModalOpen(true)
  }

  const handleSaveSection = (e: React.FormEvent) => {
    e.preventDefault()
    if (!sectionName.trim() || !currentChapterIdForSection) return

    const payload = {
      syllabus_chapter_id: currentChapterIdForSection,
      syllabus_section: {
        name: sectionName.trim(),
        section_ids: sectionMappedIds,
      }
    }

    if (editingSectionId) {
      router.patch(`/admin/syllabi/${syllabus.id}/syllabus_sections/${editingSectionId}`, payload, {
        onSuccess: () => setSectionModalOpen(false)
      })
    } else {
      router.post(`/admin/syllabi/${syllabus.id}/syllabus_sections`, payload, {
        onSuccess: () => setSectionModalOpen(false)
      })
    }
  }

  const handleDeleteSection = (secId: number) => {
    if (!confirm('Are you sure you want to remove this syllabus section?')) return
    router.delete(`/admin/syllabi/${syllabus.id}/syllabus_sections/${secId}`)
  }

  const { href: backHref, label: backLabel } = resolveBackNavigation('/admin/syllabi', 'Syllabi')

  return (
    <AppShell>
      <div className="space-y-6 max-w-6xl mx-auto">
        {/* Top bar & Navigation */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-b pb-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" asChild>
              <Link href={backHref}>
                <ArrowLeft className="mr-1.5 h-4 w-4" /> {backLabel}
              </Link>
            </Button>
            <span className="text-muted-foreground">/</span>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold tracking-tight">{syllabus.name}</h1>
              <Badge variant="outline">v{syllabus.version}</Badge>
              {syllabus.exam_name && (
                <Badge variant="secondary" className="bg-primary/10 text-primary">
                  {syllabus.exam_name}
                </Badge>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 font-medium"
              onClick={() => setImportModalOpen(true)}
            >
              <Upload className="h-3.5 w-3.5 text-muted-foreground" />
              Import JSON
            </Button>
            <Button variant="outline" size="sm" asChild className="gap-1.5 font-medium">
              <a href={`/admin/syllabi/${syllabus.id}/export`}>
                <Download className="h-3.5 w-3.5 text-muted-foreground" />
                Export JSON
              </a>
            </Button>
            <Button variant="outline" size="sm" onClick={() => setSyllabusModalOpen(true)} className="gap-1.5">
              <Pencil className="h-3.5 w-3.5" /> Edit Details
            </Button>
            <Button size="sm" onClick={openNewSubject} className="gap-1.5 font-semibold">
              <Plus className="h-4 w-4" /> Add Subject
            </Button>
          </div>

        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          <Card className="bg-muted/30">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground">Subjects</p>
                <p className="text-2xl font-bold mt-0.5">{syllabus.subjects.length}</p>
              </div>
              <BookOpen className="h-6 w-6 text-primary/60" />
            </CardContent>
          </Card>
          <Card className="bg-muted/30">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground">Chapters</p>
                <p className="text-2xl font-bold mt-0.5">{totalChapters}</p>
              </div>
              <FolderTree className="h-6 w-6 text-primary/60" />
            </CardContent>
          </Card>
          <Card className="bg-muted/30">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground">Sections</p>
                <p className="text-2xl font-bold mt-0.5">{totalSections}</p>
              </div>
              <Layers className="h-6 w-6 text-primary/60" />
            </CardContent>
          </Card>
          <Card className="bg-muted/30">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground">Question Pool</p>
                <p className="text-2xl font-bold mt-0.5">
                  {syllabus.total_questions_count ?? syllabus.subjects.reduce((acc, s) => acc + (s.questions_count || 0), 0)}
                </p>
              </div>
              <HelpCircle className="h-6 w-6 text-emerald-500/60" />
            </CardContent>
          </Card>
          <Card className="bg-muted/30">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground">Exam Target</p>
                <p className="text-sm font-bold truncate mt-1 text-foreground">{syllabus.exam_name || 'None'}</p>
              </div>
              <GraduationCap className="h-6 w-6 text-primary/60" />
            </CardContent>
          </Card>
        </div>

        {syllabus.description && (
          <Card className="bg-muted/20 border-dashed">
            <CardContent className="p-4 text-sm text-muted-foreground">
              {syllabus.description}
            </CardContent>
          </Card>
        )}

        {/* Hierarchy Tree */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold flex items-center gap-2">
              <FolderTree className="h-5 w-5 text-primary" /> Syllabus Structure
            </h2>
          </div>

          {syllabus.subjects.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center space-y-3">
                <FolderTree className="h-10 w-10 text-muted-foreground mx-auto" />
                <p className="text-sm text-muted-foreground">No subjects added to this syllabus yet.</p>
                <Button size="sm" onClick={openNewSubject} className="gap-1.5">
                  <Plus className="h-4 w-4" /> Add First Subject
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {syllabus.subjects.map((subj) => (
                <Card key={subj.id} className="border-2 shadow-sm">
                  {/* Subject Header */}
                  <CardHeader className="bg-muted/40 border-b py-3 px-5 flex flex-row items-center justify-between">
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <CardTitle className="text-lg font-bold text-foreground flex items-center gap-2">
                          <BookOpen className="h-5 w-5 text-primary shrink-0" />
                          {subj.subject_id ? (
                            <Link
                              href={`/admin/subjects/${subj.subject_id}?return_to=${encodeURIComponent(typeof window !== 'undefined' ? (window.location.pathname + window.location.search) : `/admin/syllabi/${syllabus.id}`)}`}
                              className="hover:underline flex items-center gap-1.5 text-primary"
                            >
                              <span>{subj.name}</span>
                            </Link>
                          ) : (
                            <span>{subj.name}</span>
                          )}
                        </CardTitle>

                        {/* Master Subject Resource Link */}
                        {subj.subject_id ? (
                          <Badge variant="outline" className="bg-background text-xs gap-1 border-primary/40 text-primary">
                            <Link
                              href={`/admin/subjects/${subj.subject_id}?return_to=${encodeURIComponent(typeof window !== 'undefined' ? (window.location.pathname + window.location.search) : `/admin/syllabi/${syllabus.id}`)}`}
                              className="hover:underline flex items-center gap-1"
                            >
                              <Link2 className="h-3 w-3" /> {subj.subject_name || subj.name}
                            </Link>
                          </Badge>
                        ) : subj.subject_name ? (
                          <Badge variant="outline" className="bg-background text-xs gap-1 border-primary/40 text-primary">
                            <span className="flex items-center gap-1">
                              <Link2 className="h-3 w-3" /> {subj.subject_name}
                            </span>
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs text-muted-foreground">
                            Custom Subject
                          </Badge>
                        )}

                        {/* Subject Level Analytics & Statistics Badges */}
                        <Badge variant="outline" className="bg-blue-50/50 dark:bg-blue-950/30 text-blue-800 dark:text-blue-200 border-blue-200 text-xs gap-1 font-medium">
                          <Bookmark className="h-3 w-3 text-blue-500" />
                          <span>{subj.chapters.length} Chapters</span>
                        </Badge>

                        <Badge variant="outline" className="bg-cyan-50/50 dark:bg-cyan-950/30 text-cyan-800 dark:text-cyan-200 border-cyan-200 text-xs gap-1 font-medium">
                          <FileText className="h-3 w-3 text-cyan-500" />
                          <span>{subj.chapters.reduce((acc, c) => acc + (c.sections?.length || 0), 0)} Sections</span>
                        </Badge>

                        <Badge variant="outline" className="bg-emerald-50/50 dark:bg-emerald-950/30 text-emerald-800 dark:text-emerald-200 border-emerald-200 text-xs gap-1 font-medium" asChild={!!subj.subject_id}>
                          {subj.subject_id ? (
                            <Link href={`/admin/questions?subject_ids[]=${subj.subject_id}&return_to=${encodeURIComponent(typeof window !== 'undefined' ? (window.location.pathname + window.location.search) : `/admin/syllabi/${syllabus.id}`)}`} className="hover:underline flex items-center gap-1">
                              <HelpCircle className="h-3 w-3 text-emerald-500" />
                              <span>{subj.questions_count || 0} Questions Available</span>
                            </Link>
                          ) : (
                            <span className="flex items-center gap-1">
                              <HelpCircle className="h-3 w-3 text-emerald-500" />
                              <span>{subj.questions_count || 0} Questions Available</span>
                            </span>
                          )}
                        </Badge>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openNewChapter(subj.id, subj.name)}
                        className="gap-1 text-xs font-semibold"
                      >
                        <Plus className="h-3.5 w-3.5" /> Add Chapter
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => openEditSubject(subj)}
                        title="Edit Subject"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-destructive"
                        onClick={() => handleDeleteSubject(subj.id)}
                        title="Delete Subject"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </CardHeader>

                  {/* Chapters & Sections */}
                  <CardContent className="p-4 space-y-4">
                    {subj.chapters.length === 0 ? (
                      <p className="text-xs text-muted-foreground py-2 text-center italic">
                        No chapters under {subj.name}. Click "Add Chapter" above to create one.
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {subj.chapters.map((chap) => (
                          <div
                            key={chap.id}
                            className="rounded-lg border bg-card p-4 space-y-3 transition-colors hover:border-primary/30"
                          >
                            {/* Chapter Bar */}
                            <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-2.5">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-semibold text-sm flex items-center gap-1.5">
                                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                  {chap.name}
                                </span>

                                {chap.chapter_name ? (
                                  <Badge variant="outline" className="text-[11px] gap-1 border-muted-foreground/30">
                                    <span className="flex items-center gap-1 text-muted-foreground">
                                      <Link2 className="h-2.5 w-2.5" /> {chap.chapter_name}
                                    </span>
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="text-[11px] text-muted-foreground">
                                    Custom Chapter
                                  </Badge>
                                )}

                                {/* Standards list */}
                                {chap.standards?.map((std) => (
                                  <Badge key={std.id} variant="secondary" className="text-[11px] font-semibold">
                                    <Link
                                      href={`/admin/standards/${std.id}?return_to=${encodeURIComponent(typeof window !== 'undefined' ? (window.location.pathname + window.location.search) : `/admin/syllabi/${syllabus.id}`)}`}
                                      className="hover:underline"
                                    >
                                      Std: {std.name}
                                    </Link>
                                  </Badge>
                                ))}

                                {/* Chapter Level Analytics & Stats Badges */}
                                <Badge variant="outline" className="bg-cyan-50/50 dark:bg-cyan-950/30 text-cyan-800 dark:text-cyan-200 border-cyan-200 text-[11px] gap-1 font-medium">
                                  <FileText className="h-3 w-3 text-cyan-500" />
                                  <span>{chap.sections.length} Sections</span>
                                </Badge>

                                <Badge variant="outline" className="bg-emerald-50/50 dark:bg-emerald-950/30 text-emerald-800 dark:text-emerald-200 border-emerald-200 text-[11px] gap-1 font-medium" asChild={!!chap.chapter_id}>
                                  {chap.chapter_id ? (
                                    <Link href={`/admin/questions?chapter_ids[]=${chap.chapter_id}&return_to=${encodeURIComponent(typeof window !== 'undefined' ? (window.location.pathname + window.location.search) : `/admin/syllabi/${syllabus.id}`)}`} className="hover:underline flex items-center gap-1">
                                      <HelpCircle className="h-3 w-3 text-emerald-500" />
                                      <span>{chap.questions_count || 0} Questions Available</span>
                                    </Link>
                                  ) : (
                                    <span className="flex items-center gap-1">
                                      <HelpCircle className="h-3 w-3 text-emerald-500" />
                                      <span>{chap.questions_count || 0} Questions Available</span>
                                    </span>
                                  )}
                                </Badge>
                              </div>

                              <div className="flex items-center gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 text-xs gap-1"
                                  onClick={() => openNewSection(chap.id)}
                                >
                                  <Plus className="h-3 w-3" /> Add Section
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-7 w-7"
                                  onClick={() => openEditChapter(subj.id, chap)}
                                  title="Edit Chapter"
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-7 w-7 text-destructive"
                                  onClick={() => handleDeleteChapter(chap.id)}
                                  title="Delete Chapter"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>

                            {/* Sections Grid */}
                            <div className="pl-6 pt-1">
                              {chap.sections.length === 0 ? (
                                <p className="text-xs text-muted-foreground italic">No sections defined yet.</p>
                              ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                                  {chap.sections.map((sec) => (
                                    <div
                                      key={sec.id}
                                      className="flex items-center justify-between rounded-md border bg-muted/20 px-3 py-2 text-xs hover:bg-muted/40 transition-colors"
                                    >
                                      <div className="space-y-0.5 truncate pr-2">
                                        <p className="font-medium truncate">{sec.name}</p>
                                        {sec.mapped_sections.length > 0 ? (
                                          <div className="flex flex-wrap gap-1">
                                            {sec.mapped_sections.map((ms) => (
                                              <Link
                                                key={ms.id}
                                                href="/admin/sections"
                                                className="text-[10px] text-muted-foreground hover:underline hover:text-primary flex items-center gap-0.5"
                                              >
                                                <Link2 className="h-2.5 w-2.5" /> {ms.name}
                                              </Link>
                                            ))}
                                          </div>
                                        ) : (
                                          <span className="text-[10px] text-muted-foreground italic">No master section</span>
                                        )}
                                      </div>

                                      <div className="flex items-center gap-1 shrink-0">
                                        {/* Section Level Questions Badge */}
                                        <Badge variant="outline" className="bg-emerald-50/50 dark:bg-emerald-950/30 text-emerald-800 dark:text-emerald-200 border-emerald-200 text-[10px] gap-0.5 font-medium shrink-0" asChild={sec.mapped_sections.length > 0}>
                                          {sec.mapped_sections.length > 0 ? (
                                            <Link href={`/admin/questions?section_ids[]=${sec.mapped_sections[0].id}&return_to=${encodeURIComponent(typeof window !== 'undefined' ? (window.location.pathname + window.location.search) : `/admin/syllabi/${syllabus.id}`)}`} className="hover:underline flex items-center gap-0.5">
                                              <HelpCircle className="h-2.5 w-2.5 text-emerald-500" />
                                              <span>{sec.questions_count || 0} Qs</span>
                                            </Link>
                                          ) : (
                                            <span className="flex items-center gap-0.5">
                                              <HelpCircle className="h-2.5 w-2.5 text-emerald-500" />
                                              <span>{sec.questions_count || 0} Qs</span>
                                            </span>
                                          )}
                                        </Badge>
                                        <Button
                                          size="icon"
                                          variant="ghost"
                                          className="h-6 w-6"
                                          onClick={() => openEditSection(chap.id, sec)}
                                          title="Edit Section"
                                        >
                                          <Pencil className="h-3 w-3" />
                                        </Button>
                                        <Button
                                          size="icon"
                                          variant="ghost"
                                          className="h-6 w-6 text-destructive"
                                          onClick={() => handleDeleteSection(sec.id)}
                                          title="Delete Section"
                                        >
                                          <Trash2 className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* 1. Modal: Edit Syllabus Details */}
        <Dialog open={syllabusModalOpen} onOpenChange={setSyllabusModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Syllabus Header</DialogTitle>
              <DialogDescription>Update the syllabus title, version, and exam target.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSaveSyllabus} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Exam Target *</label>
                <select
                  value={editSyllabusExamId}
                  onChange={(e) => setEditSyllabusExamId(Number(e.target.value))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  required
                >
                  {available_exams.map((ex) => (
                    <option key={ex.id} value={ex.id}>{ex.name} ({ex.code})</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Syllabus Name *</label>
                <Input
                  value={editSyllabusName}
                  onChange={(e) => setEditSyllabusName(e.target.value)}
                  placeholder="e.g. JEE Main Official 2026"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Version</label>
                <Input
                  type="number"
                  value={editSyllabusVersion}
                  onChange={(e) => setEditSyllabusVersion(Number(e.target.value))}
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Description</label>
                <Input
                  value={editSyllabusDesc}
                  onChange={(e) => setEditSyllabusDesc(e.target.value)}
                  placeholder="Optional notes or references"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setSyllabusModalOpen(false)}>Cancel</Button>
                <Button type="submit">Save Changes</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* 2. Modal: Add/Edit SyllabusSubject */}
        <Dialog open={subjectModalOpen} onOpenChange={setSubjectModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingSubjectId ? 'Edit Syllabus Subject' : 'Add Syllabus Subject'}</DialogTitle>
              <DialogDescription>Define subject name and link with master subject taxonomy.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSaveSubject} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Subject Name *</label>
                <Input
                  value={subjectName}
                  onChange={(e) => setSubjectName(e.target.value)}
                  placeholder="e.g. Physics / Physical Chemistry"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Link to Master Subject (Optional)</label>
                <select
                  value={subjectId}
                  onChange={(e) => {
                    const val = e.target.value === '' ? '' : Number(e.target.value)
                    setSubjectId(val)
                    if (val !== '' && !subjectName) {
                      const found = available_subjects.find((s) => s.id === val)
                      if (found) setSubjectName(found.name)
                    }
                  }}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">-- None (Custom Subject) --</option>
                  {available_subjects.map((s) => (
                    <option key={s.id} value={s.id}>{s.name} ({s.code})</option>
                  ))}
                </select>
              </div>

              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setSubjectModalOpen(false)}>Cancel</Button>
                <Button type="submit">{editingSubjectId ? 'Update Subject' : 'Add Subject'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* 3. Modal: Add/Edit SyllabusChapter */}
        <Dialog open={chapterModalOpen} onOpenChange={setChapterModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingChapterId ? 'Edit Syllabus Chapter' : 'Add Syllabus Chapter'}</DialogTitle>
              <DialogDescription>Define chapter name, applicable standards, and link with master chapter.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSaveChapter} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Chapter Name *</label>
                <Input
                  value={chapterName}
                  onChange={(e) => setChapterName(e.target.value)}
                  placeholder="e.g. Kinematics / Electrostatics"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Link to Master Chapter (Optional)</label>
                <select
                  value={masterChapterId}
                  onChange={(e) => {
                    const val = e.target.value === '' ? '' : Number(e.target.value)
                    setMasterChapterId(val)
                    if (val !== '') {
                      const found = filteredChapters.find((c) => c.id === val)
                      if (found) {
                        if (!chapterName) setChapterName(found.name)
                        // Auto-populate resembling standards from master chapter
                        setChapterStandardIds(found.standard_ids || [])
                      }
                    }
                  }}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">-- None (Custom Chapter) --</option>
                  {filteredChapters.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.subject_name ? `[${c.subject_name}] ` : ''}{c.name}
                    </option>
                  ))}
                </select>
                {filteredChapters.length === 0 && (
                  <p className="text-[11px] text-muted-foreground italic">
                    No master chapters found for this subject. You can still enter a custom chapter name above.
                  </p>
                )}
                {filteredChapters.length > 0 && (
                  <p className="text-[11px] text-muted-foreground">
                    Linking a master chapter will auto-fill its standards and populate its sections.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Applicable Standards</label>
                <div className="flex flex-wrap gap-2 p-2 border rounded-md bg-muted/20">
                  {available_standards.map((std) => (
                    <label key={std.id} className="flex items-center gap-1.5 text-xs cursor-pointer p-1 rounded hover:bg-muted">
                      <input
                        type="checkbox"
                        checked={chapterStandardIds.includes(std.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setChapterStandardIds([...chapterStandardIds, std.id])
                          } else {
                            setChapterStandardIds(chapterStandardIds.filter((id) => id !== std.id))
                          }
                        }}
                      />
                      <span>{std.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setChapterModalOpen(false)}>Cancel</Button>
                <Button type="submit">{editingChapterId ? 'Update Chapter' : 'Add Chapter'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* 4. Modal: Add/Edit SyllabusSection */}
        <Dialog open={sectionModalOpen} onOpenChange={setSectionModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingSectionId ? 'Edit Syllabus Section' : 'Add Syllabus Section'}</DialogTitle>
              <DialogDescription>Define section name and map to master taxonomy sections.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSaveSection} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Section Name *</label>
                <Input
                  value={sectionName}
                  onChange={(e) => setSectionName(e.target.value)}
                  placeholder="e.g. Vectors and Projectile Motion"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Map Master Sections</label>
                {filteredSections.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic p-2 border rounded-md bg-muted/20">
                    No master sections available for this chapter.
                  </p>
                ) : (
                  <div className="max-h-48 overflow-y-auto p-2 border rounded-md space-y-1 bg-muted/20">
                    {filteredSections.map((sec) => (
                      <label key={sec.id} className="flex items-center gap-2 text-xs cursor-pointer p-1 rounded hover:bg-muted">
                        <input
                          type="checkbox"
                          checked={sectionMappedIds.includes(sec.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSectionMappedIds([...sectionMappedIds, sec.id])
                            } else {
                              setSectionMappedIds(sectionMappedIds.filter((id) => id !== sec.id))
                            }
                          }}
                        />
                        <span>{sec.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setSectionModalOpen(false)}>Cancel</Button>
                <Button type="submit">{editingSectionId ? 'Update Section' : 'Add Section'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Import Syllabus Modal */}
        <ImportSyllabusModal
          open={importModalOpen}
          onOpenChange={setImportModalOpen}
          targetSyllabusId={syllabus.id}
        />
      </div>
    </AppShell>
  )
}

